<!--
 * @Author: 'lek' '2889356275@qq.com'
 * @Date: 2025-12-04 16:45:00
 * @LastEditors: 'lek' '2889356275@qq.com'
 * @LastEditTime: 2025-12-04 16:45:00
 * @FilePath: \巴斯德\公司网站\bsd_website\src\views\CoreProducts.vue
 * @Description: 核心产品页面
-->
<template>
  <div class="app">
    <CommonHeader :headerData="headerData" @nav-change="handleNavChange" />

    <!-- 首屏大图区 - 动态对比表格 -->
    <section class="hero" ref="hero">
      <!-- 巴斯德定位进度条 -->
      <div class="positioning-progress" :class="{ 'slide-in': heroVisible }">
        <div class="positioning-label">{{ $t('products.positioning') }}：</div>
        <div class="positioning-track">
          <div class="positioning-bar" :style="{ width: positioningProgress + '%' }"></div>
        </div>
      </div>

      <!-- 动态对比表格 -->
      <div class="comparison-table" :class="{ 'fade-in': heroVisible }">
        <!-- X轴标题 -->
        <div class="x-axis">
          <div class="axis-item" v-for="(stage, index) in localizedDetectionStages" :key="index">
            <div class="stage-content">
              <div class="stage-split" v-if="index === 0">
                <div class="stage-upper">{{ stage.upperText }}</div>
                <div class="stage-lower">{{ stage.lowerText }}</div>
              </div>
              <div class="stage-split" v-else-if="index === 2">
                <div class="stage-upper">{{ stage.upperText }}</div>
                <div class="stage-lower">{{ stage.lowerText }}</div>
              </div>
              <div class="stage-split" v-else>
                <div class="stage-upper">{{ stage.upperText }}</div>
                <div class="stage-lower">{{ stage.lowerText }}</div>
              </div>
            </div>
            <!-- 连接箭头 -->
            <div v-if="index < detectionStages.length - 1" class="connector-arrow">
              <svg width="100" height="85" viewBox="0 0 100 35">
                <defs>
                  <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style="stop-color:#00D4B4;stop-opacity:0.6" />
                    <stop offset="50%" style="stop-color:#00D4B4;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#00B894;stop-opacity:1" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <!-- 主体箭头路径 -->
                <path d="M 8 17.5 L 75 17.5" stroke="url(#arrowGradient)" stroke-width="2" fill="none" stroke-linecap="round" filter="url(#glow)"/>
                <!-- 箭头头部 -->
                <path d="M 75 17.5 L 85 12 M 75 17.5 L 85 23" stroke="url(#arrowGradient)" stroke-width="2" fill="none" stroke-linecap="round" filter="url(#glow)"/>
                <!-- 装饰性流线 -->
                <path d="M 10 17.5 Q 30 10, 50 17.5 T 70 17.5" stroke="url(#arrowGradient)" stroke-width="2" fill="none" stroke-linecap="round" opacity="0.6"/>
                <!-- 起点圆圈 -->
                <circle cx="8" cy="17.5" r="4" fill="#00D4B4" filter="url(#glow)"/>
                <circle cx="8" cy="17.5" r="2" fill="#ffffff"/>
                <!-- 终点圆圈 -->
                <circle cx="75" cy="17.5" r="4" fill="#00B894" filter="url(#glow)"/>
                <circle cx="75" cy="17.5" r="2" fill="#ffffff"/>
                <!-- 动画流光点 -->
                <circle r="2" fill="#ffffff" filter="url(#glow)">
                  <animateMotion dur="2s" repeatCount="indefinite">
                    <mpath href="#flowPath"/>
                  </animateMotion>
                </circle>
                <path id="flowPath" d="M 8 17.5 L 75 17.5" stroke="none" fill="none"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Y轴和对比内容 -->
        <div class="comparison-content">
          <!-- 国外现状 -->
          <div class="comparison-row foreign">
            <div class="y-axis-label">
              <div class="label-content">
                <div class="country-flag">🌍</div>
                <span>{{ $t('products.foreignStatus') }}</span>
              </div>
            </div>
            <div class="progress-track">
              <div class="progress-bar foreign-bar" :style="{ width: foreignProgress + '%' }"></div>
            </div>
          </div>

          <!-- 国内现状 -->
          <div class="comparison-row domestic">
            <div class="y-axis-label">
              <div class="label-content">
                <div class="country-flag">🇨🇳</div>
                <span>{{ $t('products.domesticStatus') }}</span>
              </div>
            </div>
            <div class="progress-track">
              <div class="progress-bar domestic-bar" :style="{ width: domesticProgress + '%' }"></div>
            </div>
          </div>
        </div>

        <!-- 动态粒子效果背景 -->
        <div class="particles">
          <div v-for="particle in particles" :key="particle.id"
               class="particle"
               :style="{
                 left: particle.x + '%',
                 top: particle.y + '%',
                 animationDelay: particle.delay + 's',
                 animationDuration: particle.duration + 's'
               }">
          </div>
        </div>
        
        <!-- 分子动画效果 -->
        <div class="molecular-effect">
          <div class="molecular-particle"></div>
          <div class="molecular-particle"></div>
          <div class="molecular-particle"></div>
          <div class="molecular-particle"></div>
          <div class="molecular-particle"></div>
        </div>
      </div>
    </section>

    <main class="main-content">
      <div class="container">
        <!-- 产品概览模块 -->
        <section class="section" v-intersection="onOverviewVisible">
          <div class="section-subtitle">{{ localizedOverviewData.subtitle }}</div>
          <h2 class="section-title">{{ localizedOverviewData.title }}</h2>
          
          <div class="overview-content">
            <!-- 左侧文字介绍 -->
            <div class="overview-text" :class="{ 'slide-in-left': sectionsVisible.overview }">
              <p v-for="(paragraph, index) in localizedOverviewData.description" :key="index">
                {{ paragraph }}
              </p>
              <div class="key-features">
                <div 
                  v-for="(feature, index) in localizedOverviewData.features"
                  :key="index"
                  class="feature-item"
                >
                  <span class="feature-icon">{{ feature.icon }}</span>
                  <span class="feature-text">{{ feature.text }}</span>
                </div>
              </div>
            </div>
            
            <!-- 右侧数据展示 -->
            <div class="overview-stats" :class="{ 'slide-in-right': sectionsVisible.overview }">
              <div 
                v-for="(stat, index) in localizedOverviewData.stats"
                :key="index"
                class="stat-item"
              >
                <div class="stat-number">{{ stat.number }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 核心产品展示模块 -->
        <section class="section" v-intersection="onProductsVisible">
          <div class="section-subtitle">{{ localizedProductsData.subtitle }}</div>
          <h2 class="section-title">{{ localizedProductsData.title }}</h2>
          
          <div class="products-grid">
            <div 
              v-for="(product, index) in localizedProductsData.items"
              :key="index"
              class="product-card" 
              :class="{ 'fade-in-up': sectionsVisible.products }"
              :style="{ animationDelay: `${index * 0.2}s` }"
              @mouseenter="product.hovered = true"
              @mouseleave="product.hovered = false"
            >
              <!-- 产品图标 -->
              <div class="product-icon" :class="{ 'icon-hover': product.hovered }">
                <div class="icon-wrapper">
                  <span class="product-emoji">{{ product.icon }}</span>
                </div>
                <div class="product-overlay">
                  <button class="detail-btn" @click="viewProductDetail(product)">
                    {{ $t('products.viewDetails') }}
                  </button>
                </div>
              </div>
              
              <!-- 产品信息 -->
              <div class="product-info">
                <div class="product-category">{{ product.category }}</div>
                <h3 class="product-name">{{ product.name }}</h3>
                <p class="product-desc">{{ product.description }}</p>
                 
                <!-- 产品规格 -->
                <div class="product-specs">
                  <div 
                    v-for="(spec, index) in product.specs" 
                    :key="index"
                    class="spec-item"
                  >
                    <span class="spec-label">{{ spec.label }}:</span>
                    <span class="spec-value">{{ spec.value }}</span>
                  </div>
                </div>
                
                <!-- 产品标签 -->
                <div class="product-tags">
                  <span 
                    v-for="(tag, index) in product.tags" 
                    :key="index"
                    class="tag"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 技术优势模块 -->
        <section class="section" v-intersection="onAdvantagesVisible">
          <div class="section-subtitle">{{ localizedAdvantagesData.subtitle }}</div>
          <h2 class="section-title">{{ localizedAdvantagesData.title }}</h2>
          
          <div class="advantages-container">
            <div 
              v-for="(advantage, index) in localizedAdvantagesData.items"
              :key="index"
              class="advantage-item"
              :class="{ 'fade-in-up': sectionsVisible.advantages }"
              :style="{ animationDelay: `${index * 0.15}s` }"
            >
              <div class="advantage-icon">
                <span class="advantage-emoji">{{ advantage.icon }}</span>
              </div>
              <h4 class="advantage-title">{{ advantage.title }}</h4>
              <p class="advantage-desc">{{ advantage.description }}</p>
              <div class="advantage-metrics">
                <div 
                  v-for="(metric, index) in advantage.metrics" 
                  :key="index"
                  class="metric"
                >
                  <span class="metric-value">{{ metric.value }}</span>
                  <span class="metric-unit">{{ metric.unit }}</span>
                  <span class="metric-label">{{ metric.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 应用场景模块 -->
        <section class="section" v-intersection="onApplicationsVisible">
          <div class="section-subtitle">{{ localizedApplicationsData.subtitle }}</div>
          <h2 class="section-title">{{ localizedApplicationsData.title }}</h2>
          
          <div class="applications-showcase">
            <!-- 左侧场景列表 -->
            <div class="applications-list" :class="{ 'slide-in-left': sectionsVisible.applications }">
              <div 
                v-for="(app, index) in localizedApplicationsData.items"
                :key="index"
                class="application-item"
                :class="{ active: selectedApplication === index }"
                @click="selectedApplication = index"
                @mouseenter="selectedApplication = index"
              >
                <div class="app-icon">
                  <span class="app-emoji">{{ app.icon }}</span>
                </div>
                <div class="app-content">
                  <h4>{{ app.title }}</h4>
                  <p>{{ app.brief }}</p>
                </div>
              </div>
            </div>
            
            <!-- 右侧详细展示 -->
            <div class="application-detail" :class="{ 'slide-in-right': sectionsVisible.applications }">
              <div class="detail-content">
                <img 
                  :src="localizedApplicationsData.items[selectedApplication].image"
                  :alt="localizedApplicationsData.items[selectedApplication].title"
                  class="detail-image"
                />
                <h3>{{ localizedApplicationsData.items[selectedApplication].title }}</h3>
                <p>{{ localizedApplicationsData.items[selectedApplication].description }}</p>
                <div class="detail-features">
                  <div 
                    v-for="(feature, index) in localizedApplicationsData.items[selectedApplication].features"
                    :key="index"
                    class="detail-feature"
                  >
                    <span class="feature-check">✓</span>
                    <span>{{ feature }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </main>

    <CommonFooter />
    <BackToTop />
  </div>
</template>

<script>
import CommonHeader from '../components/CommonHeader.vue';
import CommonFooter from '../components/CommonFooter.vue';
import BackToTop from '../components/BackToTop.vue';

export default {
  name: 'CoreProducts',

  components: {
    CommonHeader,
    CommonFooter,
    BackToTop
  },

  data() {
    return {
      // 头部数据配置
      headerData: {
        logo: {
          title: 'PASTEUR',
          imageUrl: require('@/assets/image/logo.png')
        },
        navigation: [
          { key: 'welcome', active: false, hovered: false },
          { key: 'company', active: false, hovered: false },
          { key: 'products', active: true, hovered: false },
          { key: 'links', active: false, hovered: false }
        ]
      },

      // 检测发展阶段数据
      detectionStages: [
        { title: '', upperText: 'Traditional Manual', lowerText: 'Manual Detection' },
        { title: '', upperText: 'Semi-Automated', lowerText: 'Machine Detection' },
        { title: '', upperText: 'Fully Automated', lowerText: 'Process Monitoring' },
        { title: '', upperText: 'AI-Enhanced Detection', lowerText: 'Data Assetization' }
      ],

      // 动态粒子数据
      particles: [
        { id: 1, x: 10, y: 20, delay: 0, duration: 3 },
        { id: 2, x: 85, y: 15, delay: 0.5, duration: 4 },
        { id: 3, x: 25, y: 80, delay: 1, duration: 3.5 },
        { id: 4, x: 75, y: 85, delay: 1.5, duration: 4.5 },
        { id: 5, x: 50, y: 50, delay: 2, duration: 3 },
        { id: 6, x: 15, y: 60, delay: 2.5, duration: 5 },
        { id: 7, x: 90, y: 40, delay: 0.8, duration: 3.8 },
        { id: 8, x: 35, y: 30, delay: 1.8, duration: 4.2 }
      ],

      // 动画进度控制
      foreignProgress: 0,
      domesticProgress: 0,
      positioningProgress: 0,

      // 产品概览数据
      overviewData: {
        subtitle: 'PRODUCT OVERVIEW',
        title: '产品概览',
        description: [
          '巴斯德医疗设备有限公司专注于微生物实验室智能化解决方案的研发与应用。我们的LIMS微生物实验室管理系统获得多项专利，为食品、药品、滤膜等样品检测提供全流程智能化管理。',
          '公司旗下拥有完整的微生物检测产品线，包括样品处理设备、微生物培养孵育设备、微生物计数设备和微生物快检设备，所有产品均具备专利技术优势，实现从样品接收到结果输出的全自动化检测流程。'
        ],
        features: [
          { icon: '🏢', text: 'LIMS系统专利技术，设备兼容性强' },
          { icon: '🧪', text: '倾注法专利，样品处理标准化' },
          { icon: '🌡️', text: '温湿度控制专利，培养环境精准' },
          { icon: '📈', text: '微生物识别专利，计数分类智能化' }
        ],
        stats: [
          { number: '10+', label: '核心专利技术' },
          { number: '4大', label: '产品系列' },
          { number: '90%', label: '自动化覆盖' },
          { number: 'GMP', label: '合规标准' }
        ]
      },

      // 核心产品数据
      productsData: {
        subtitle: 'FLAGSHIP PRODUCTS',
        title: '核心产品',
        items: [
          {
            category: 'LIMS管理系统',
            name: 'LIMS微生物实验室管理系统',
            description: '专利技术构建的实验室信息管理系统，实现设备兼容和全流程自动化管理。支持食品、药品、滤膜等样品的标准化检测流程。',
            icon: '🖥️',
            iconType: 'emoji',
            specs: [
              { label: '专利技术', value: 'LIMS系统构建' },
              { label: '设备兼容', value: '全系列设备' },
              { label: '样品支持', value: '食品/药品/滤膜' }
            ],
            tags: ['专利技术', '设备兼容', '全流程管理'],
            hovered: false
          },
          {
            category: '样品处理设备',
            name: '智能样品处理系统',
            description: '采用倾注法专利技术的样品预处理设备，配备精密温湿度控制系统，确保样品处理的标准化和一致性。',
            icon: '🧫',
            iconType: 'emoji',
            specs: [
              { label: '专利技术', value: '倾注法相关' },
              { label: '温控精度', value: '±0.1°C' },
              { label: '湿度控制', value: '±2% RH' }
            ],
            tags: ['倾注法专利', '温湿度控制', '标准化处理'],
            hovered: false
          },
          {
            category: '培养孵育设备',
            name: '微生物培养孵育系统',
            description: '集成微生物孵育、厌氧孵育和温湿度控制专利技术，为微生物培养提供最佳生长环境，提高培养效率和成功率。',
            icon: '🦠',
            iconType: 'emoji',
            specs: [
              { label: '专利技术', value: '微生物孵育/厌氧' },
              { label: '温度范围', value: '4-60°C' },
              { label: '培养容量', value: '100-500个样品' }
            ],
            tags: ['微生物孵育专利', '厌氧培养', '精准控温'],
            hovered: false
          },
          {
            category: '计数识别设备',
            name: '微生物智能计数系统',
            description: '基于微生物计数、识别和分类专利技术的智能化设备，实现菌落的自动识别、计数和分类，大幅提高检测效率。',
            icon: '🔍',
            iconType: 'emoji',
            specs: [
              { label: '专利技术', value: '计数/识别/分类' },
              { label: '识别精度', value: '≥99.5%' },
              { label: '计数速度', value: '<30秒/皿' }
            ],
            tags: ['微生物计数专利', '智能识别', '自动分类'],
            hovered: false
          },
          {
            category: '快速检测设备',
            name: '微生物快速检测系统',
            description: '集成微生物快检、菌落回收率和GMP相关专利技术，实现微生物的快速检测和质量控制，满足GMP标准要求。',
            icon: '⏱️',
            iconType: 'emoji',
            specs: [
              { label: '专利技术', value: '快检/回收率/GMP' },
              { label: '检测时间', value: '2-8小时' },
              { label: 'GMP合规', value: '100%符合' }
            ],
            tags: ['微生物快检专利', '菌落回收率', 'GMP标准'],
            hovered: false
          }
        ]
      },

      // 技术优势数据
      advantagesData: {
        subtitle: 'TECHNICAL ADVANTAGES',
        title: '技术优势',
        items: [
          {
            title: 'LIMS系统专利',
            description: '自主研发的LIMS微生物实验室管理系统专利技术，实现设备间无缝兼容和数据互通，构建完整的智能化检测生态。',
            icon: '🔗',
            iconType: 'emoji',
            metrics: [
              { value: '10+', unit: '项', label: 'LIMS专利' },
              { value: '100', unit: '%', label: '设备兼容性' }
            ]
          },
          {
            title: '倾注法专利技术',
            description: '独创的倾注法样品处理专利技术，结合温湿度控制系统，确保样品处理的标准化和重复性。',
            icon: '🌡️',
            iconType: 'emoji',
            metrics: [
              { value: '±0.1', unit: '°C', label: '温控精度' },
              { value: '±2', unit: '%RH', label: '湿度精度' }
            ]
          },
          {
            title: '微生物孵育专利',
            description: '集成微生物孵育和厌氧孵育专利技术，提供最佳培养环境，显著提高微生物检测的准确性和效率。',
            icon: '🥚',
            iconType: 'emoji',
            metrics: [
              { value: '99.5', unit: '%', label: '培养成功率' },
              { value: '24-72', unit: '小时', label: '培养周期' }
            ]
          },
          {
            title: '智能识别专利',
            description: '基于微生物计数、识别和分类专利技术，实现菌落的自动化检测，大幅提升检测效率和准确率。',
            icon: '🎯',
            iconType: 'emoji',
            metrics: [
              { value: '99.5', unit: '%', label: '识别准确率' },
              { value: '<30', unit: '秒', label: '检测速度' }
            ]
          },
          {
            title: '快检技术专利',
            description: '微生物快检、菌落回收率和GMP相关专利技术，实现快速检测同时确保结果符合GMP标准要求。',
            icon: '⚡',
            iconType: 'emoji',
            metrics: [
              { value: '2-8', unit: '小时', label: '快检时间' },
              { value: '100', unit: '%', label: 'GMP合规' }
            ]
          }
        ]
      },

      // 应用场景数据
      applicationsData: {
        subtitle: 'APPLICATION SCENARIOS',
        title: '应用场景',
        items: [
          {
            title: '食品检测',
            brief: '食品微生物安全检测',
            description: '为食品企业提供专业的微生物检测解决方案，通过LIMS系统实现从样品接收到结果输出的全流程管理，确保食品安全。',
            icon: '🍎',
            iconType: 'emoji',
            image: require("@/assets/image/background.png"),
            features: [
              '食品样品LIMS管理',
              '倾注法标准化处理',
              '微生物智能计数',
              'GMP合规快检'
            ]
          },
          {
            title: '药品检测',
            brief: '药品微生物限度检查',
            description: '为制药企业提供符合GMP标准的微生物检测服务，采用专利技术确保药品生产过程中的微生物质量控制。',
            icon: '💊',
            iconType: 'emoji',
            image: require("@/assets/image/background.png"),
            features: [
              '药品样品追踪管理',
              '无菌检查自动化',
              '微生物限度检测',
              'GMP标准合规'
            ]
          },
          {
            title: '滤膜检测',
            brief: '滤膜微生物截留效率测试',
            description: '为滤膜生产企业提供专业的微生物检测服务，通过专利的倾注法和快检技术，评估滤膜的微生物截留性能。',
            icon: '🔬',
            iconType: 'emoji',
            image: require("@/assets/image/background.png"),
            features: [
              '滤膜样品标准化',
              '截留效率检测',
              '菌落回收率测试',
              '数据分析报告'
            ]
          },
          {
            title: '实验室管理',
            brief: '微生物实验室整体解决方案',
            description: '为各类微生物实验室提供LIMS系统和配套设备，实现实验室数字化转型，提升检测效率和管理水平。',
            icon: '🏢',
            iconType: 'emoji',
            image: require("@/assets/image/background.png"),
            features: [
              'LIMS系统部署',
              '设备集成兼容',
              '流程标准化管理',
              '数据资产化管理'
            ]
          }
        ]
      },
      selectedApplication: 0,

      // 组件状态管理
      heroVisible: false,
      sectionsVisible: {
        overview: false,
        products: false,
        advantages: false,
        applications: false
      }
    }
  },

  computed: {
    // 国际化数据
    localizedOverviewData() {
      return {
        subtitle: this.$t('products.overview.subtitle'),
        title: this.$t('products.overview.title'),
        description: this.$t('products.overview.description'),
        features: this.$t('products.overview.features'),
        stats: this.$t('products.overview.stats')
      }
    },

    localizedProductsData() {
      return {
        subtitle: this.$t('products.flagship.subtitle'),
        title: this.$t('products.flagship.title'),
        items: this.$t('products.flagship.items')
      }
    },

    localizedAdvantagesData() {
      return {
        subtitle: this.$t('products.advantages.subtitle'),
        title: this.$t('products.advantages.title'),
        items: this.$t('products.advantages.items')
      }
    },

    localizedApplicationsData() {
      return {
        subtitle: this.$t('products.scenarios.subtitle'),
        title: this.$t('products.scenarios.title'),
        items: this.$t('products.scenarios.items')
      }
    },

    localizedDetectionStages() {
      return [
        {
          title: '',
          upperText: this.$t('products.detectionStages.stages.0.upperText'),
          lowerText: this.$t('products.detectionStages.stages.0.lowerText')
        },
        {
          title: '',
          upperText: this.$t('products.detectionStages.stages.1.upperText'),
          lowerText: this.$t('products.detectionStages.stages.1.lowerText')
        },
        {
          title: '',
          upperText: this.$t('products.detectionStages.stages.2.upperText'),
          lowerText: this.$t('products.detectionStages.stages.2.lowerText')
        },
        {
          title: '',
          upperText: this.$t('products.detectionStages.stages.3.upperText'),
          lowerText: this.$t('products.detectionStages.stages.3.lowerText')
        }
      ]
    }
  },

  // 生命周期钩子
  mounted() {
    this.initAnimations();
  },

  methods: {
    // 处理导航变化
    handleNavChange(index) {
      const routes = ['/welcome', '/company', '/products', '/links'];
      if (index >= 0 && index < routes.length) {
        const targetRoute = routes[index];
        if (this.$route.path !== targetRoute) {
          this.$router.push(targetRoute);
        }
      }
    },

    // 初始化动画
    initAnimations() {
      setTimeout(() => {
        this.heroVisible = true;
        // 启动进度条动画
        this.startProgressAnimation();
      }, 300);
    },

    // 启动进度条动画
    startProgressAnimation() {
      // 巴斯德定位进度：到"检测算法AI化"位置 (约87.5%)
      setTimeout(() => {
        this.positioningProgress = 87.5;
      }, 600);

      // 国外进度：到"全自动全过程监测"位置 (约62.5%)
      setTimeout(() => {
        this.foreignProgress = 62.5;
      }, 800);

      // 国内进度：到"手工检测"和"半自动机器检测"中间位置 (约12.5%)
      setTimeout(() => {
        this.domesticProgress = 12.5;
      }, 1200);
    },

    // 模块可见性回调函数
    onOverviewVisible() {
      this.sectionsVisible.overview = true;
    },

    onProductsVisible() {
      this.sectionsVisible.products = true;
    },

    onAdvantagesVisible() {
      this.sectionsVisible.advantages = true;
    },

    onApplicationsVisible() {
      this.sectionsVisible.applications = true;
    },

    // 查看产品详情
    viewProductDetail(product) {
      console.log('查看产品详情:', product.name);
      // 这里可以添加跳转到产品详情页的逻辑
    }
  },

  // 自定义指令
  directives: {
    // 元素视口检测指令
    intersection: {
      inserted(el, binding) {
        const options = {
          threshold: 0.3
        };
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // 触发回调函数
              binding.value();
              observer.unobserve(el);
            }
          });
        }, options);
        
        observer.observe(el);
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  font-family: 'Microsoft YaHei', sans-serif;
  background: #f8f9fa;
}

/* 响应式容器 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.container1 {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
}

/* 左侧文字内容 */
.hero-content {
  flex: 1;
  max-width: 500px;
  z-index: 10;
  opacity: 0;
  transform: translateX(-50px);
  transition: all 1.5s ease-out;
}

.hero-content.fade-in {
  opacity: 1;
  transform: translateX(0);
}

.hero-title {
  font-size: 44px;
  color: #00D4B4;
  margin-bottom: 10px;
  font-weight: 500;
  text-shadow: 0 4px 8px rgba(6, 231, 111, 0.3);
}

.hero-subtitle {
  font-size: 13px;
  color: #00D4B4;
  margin-bottom: 40px;
  font-weight: 600;
}

/* 右侧培养皿容器 */
.petri-dish-container {
  flex: 1;
  position: relative;
  width: 400px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 培养皿底部 */
.petri-dish {
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border: 6px solid rgba(173, 168, 168, 0.2);
  backdrop-filter: blur(10px);
  animation: dishRotate 50s linear infinite;
}

/* 培养皿盖子 */
.petri-lid {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03));
  border: 6px solid rgba(172, 169, 169, 0.15);
  backdrop-filter: blur(5px);
  animation: lidRotate 25s linear infinite reverse;
}

/* 分子点 - 培养皿内部 */
.molecule {
  position: absolute;
  background: #00D4B4;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 212, 180, 0.6);
}

/* 大分子 */
.molecule-large {
  width: 12px;
  height: 12px;
  animation: moleculeFloat 3s ease-in-out infinite;
}

/* 中等分子 */
.molecule-medium {
  width: 8px;
  height: 8px;
  animation: moleculeFloat 4s ease-in-out infinite;
}

/* 小分子 */
.molecule-small {
  width: 6px;
  height: 6px;
  animation: moleculeFloat 5s ease-in-out infinite;
}

/* 分子位置 */
.molecule-1 { top: 30%; left: 25%; animation-delay: 0s; }
.molecule-2 { top: 20%; left: 60%; animation-delay: 0.5s; }
.molecule-3 { top: 45%; left: 70%; animation-delay: 1s; }
.molecule-4 { top: 65%; left: 20%; animation-delay: 1.5s; }
.molecule-5 { top: 70%; left: 55%; animation-delay: 2s; }
.molecule-6 { top: 25%; left: 40%; animation-delay: 2.5s; }
.molecule-7 { top: 55%; left: 35%; animation-delay: 3s; }
.molecule-8 { top: 35%; left: 75%; animation-delay: 0.3s; }
.molecule-9 { top: 75%; left: 75%; animation-delay: 1.8s; }
.molecule-10 { top: 15%; left: 75%; animation-delay: 2.3s; }

/* 动画效果 */
@keyframes dishRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes lidRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes moleculeFloat {
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-15px) scale(1.1);
    opacity: 1;
  }
}

/* 发光效果 */
.petri-dish::before {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 50%;
  background: linear-gradient(45deg, #00D4B4, #667eea);
  opacity: 0.3;
  z-index: -1;
}

/* 首屏动态对比表格样式 */
.hero {
  position: relative;
  background:
    radial-gradient(ellipse at 30% 30%, rgba(250, 250, 250, 0.8) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 70%, rgba(248, 249, 250, 0.6) 0%, transparent 60%),
    linear-gradient(135deg,
      #fafafa 0%,
      #f8f9fa 50%,
      #fafafa 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin-top: 80px;
  padding: 40px 20px;
}


/* 巴斯德定位进度条 */
.positioning-progress {
  position: relative;
  width: 100%;
  max-width: 1100px;
  margin-bottom: 80px;
  opacity: 0;
  transform: translateY(-30px) scale(0.9);
  transition: all 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.positioning-progress.slide-in {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.positioning-label {
  display: block;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #00D4B4 0%, #00B894 50%, #00D4B4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 38px;
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 2px 10px rgba(0, 212, 180, 0.3);
  animation: textShimmer 3s ease-in-out infinite;
  text-align: left;
}

.positioning-track {
  position: relative;
  width: 100%;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.positioning-bar {
  height: 100%;
  border-radius: 20px;
  transition: width 2s ease-out;
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, #00D4B4, #00B894, #00D4B4);
  box-shadow: 0 0 20px rgba(0, 212, 180, 0.5);
}

.positioning-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: progressShine 2s ease-in-out infinite;
}


/* 动态对比表格容器 */
.comparison-table {
  position: relative;
  width: 100%;
  max-width: 1200px;
  background:
    rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(5px);
  border-radius: 20px;
  border: 1px solid rgba(0, 212, 180, 0.05);
  padding: 50px;
  box-shadow:
    0 15px 30px rgba(0, 0, 0, 0.03),
    0 0 0 1px rgba(255, 255, 255, 0.9) inset;
  opacity: 0;
  transform: scale(0.9) translateY(20px);
  transition: all 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
}

.comparison-table.fade-in {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* 添加表格发光效果 */
.comparison-table::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background: linear-gradient(45deg,
    rgba(0, 212, 180, 0.08) 0%,
    rgba(0, 184, 148, 0.05) 25%,
    rgba(0, 212, 180, 0.06) 50%,
    rgba(0, 184, 148, 0.05) 75%,
    rgba(0, 212, 180, 0.08) 100%);
  border-radius: 20px;
  z-index: -1;
  opacity: 0;
  animation: tableGlow 8s ease-in-out infinite;
}

@keyframes tableGlow {
  0%, 100% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.002);
  }
}

/* X轴标题 */
.x-axis {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 60px;
  position: relative;
}

.axis-item {
  flex: 1;
  text-align: center;
  position: relative;
  padding: 0 5px;
}

.stage-content {
  background:
    rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 212, 180, 0.08);
  border-radius: 12px;
  padding: 20px 15px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(2px);
}

.stage-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(0, 212, 180, 0.1), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.stage-content:hover::before {
  transform: translateX(100%);
}

.stage-content:hover {
  transform: translateY(-3px);
  box-shadow:
    0 10px 20px rgba(0, 212, 180, 0.06),
    0 0 10px rgba(0, 212, 180, 0.04);
  border-color: rgba(0, 212, 180, 0.2);
  background:
    rgba(255, 255, 255, 0.95);
}

.stage-title {
  background: linear-gradient(135deg, #00D4B4 0%, #00B894 50%, #00D4B4 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.4;
  text-shadow: 0 0 20px rgba(0, 212, 180, 0.5);
  animation: textShimmer 3s ease-in-out infinite;
  position: relative;
}

.stage-title::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00D4B4, transparent);
  border-radius: 1px;
  animation: underlineGlow 2s ease-in-out infinite;
}

.stage-desc {
  color: #66778a;
  font-size: 13px;
  line-height: 1.3;
}

/* 上下分层文字样式 */
.stage-split {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.stage-split::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 0;
  background: linear-gradient(180deg, transparent, #00D4B4, transparent);
  border-radius: 1px;
  transition: height 0.3s ease;
}

.stage-content:hover .stage-split::before {
  height: 60%;
}

.stage-upper {
  background: linear-gradient(135deg, #00D4B4 0%, #00B894 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
  text-shadow: 0 0 15px rgba(0, 212, 180, 0.4);
  animation: textShimmer 3s ease-in-out infinite;
  position: relative;
}

.stage-upper::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 180, 0.6), transparent);
  border-radius: 0.5px;
}

.stage-lower {
  background: linear-gradient(135deg, #00D4B4 0%, #00B894 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  text-shadow: 0 0 12px rgba(0, 212, 180, 0.3);
  animation: textShimmer 3s ease-in-out infinite 0.5s;
  opacity: 0.9;
}

/* 连接箭头 */
.connector-arrow {
  position: absolute;
  top: 50%;
  right: -60px;
  transform: translateY(-50%);
  z-index: 2;
}

.connector-arrow svg {
  filter: drop-shadow(0 0 15px rgba(0, 212, 180, 0.5));
  animation: arrowPulse 2s ease-in-out infinite;
}

/* 对比内容区域 */
.comparison-content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.comparison-row {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
}

.y-axis-label {
  width: 140px;
  max-width: 140px;
  flex-shrink: 0;
}

.label-content {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 212, 180, 0.08);
  border: 2px solid rgba(0, 212, 180, 0.15);
  border-radius: 12px;
  padding: 12px 15px;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}

.label-content:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateX(5px);
}

.country-flag {
  font-size: 20px;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.3));
}

.label-content span {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

/* 进度轨道 */
.progress-track {
  flex: 1;
  position: relative;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 20px;
  transition: width 2s ease-out;
  position: relative;
  overflow: hidden;
}

.foreign-bar {
  background: linear-gradient(90deg, #00D4B4, #00B894, #00D4B4);
  box-shadow: 0 0 20px rgba(0, 212, 180, 0.5);
}

.domestic-bar {
  background: linear-gradient(90deg, #DC143C, #FF6347, #FF7F50);
  box-shadow: 0 0 20px rgba(220, 20, 60, 0.5);
}

.progress-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: progressShine 2s ease-in-out infinite;
}


/* 增强的动态粒子效果 */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  border-radius: 28px;
  z-index: 1;
}

.particle {
  position: absolute;
  background: radial-gradient(circle, rgba(0, 212, 180, 0.7) 0%, rgba(0, 184, 148, 0.4) 40%, transparent 80%);
  border-radius: 50%;
  animation: particleFloat linear infinite;
  box-shadow:
    0 0 12px rgba(0, 212, 180, 0.5),
    0 0 25px rgba(0, 212, 180, 0.2);
}

/* 增强粒子动画 */
@keyframes particleFloat {
  0% {
    transform: translateY(100vh) translateX(0) scale(0);
    opacity: 0;
    filter: brightness(1);
  }
  8% {
    opacity: 0.7;
    transform: translateY(85vh) translateX(8px) scale(0.8);
    filter: brightness(1.1);
  }
  25% {
    opacity: 0.9;
    transform: translateY(70vh) translateX(-8px) scale(1);
    filter: brightness(1.2);
  }
  50% {
    opacity: 1;
    transform: translateY(45vh) translateX(-12px) scale(1.1);
    filter: brightness(1.3);
  }
  75% {
    opacity: 0.8;
    transform: translateY(20vh) translateX(6px) scale(0.9);
    filter: brightness(1.1);
  }
  92% {
    opacity: 0.6;
    transform: translateY(5vh) translateX(3px) scale(0.6);
    filter: brightness(1);
  }
  100% {
    transform: translateY(-10vh) translateX(0) scale(0);
    opacity: 0;
  }
}

/* 添加分子动画效果 */
.molecular-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}

.molecular-particle {
  position: absolute;
  width: 8px;
  height: 8px;
  background: radial-gradient(circle, rgba(0, 212, 180, 0.95) 0%, rgba(0, 184, 148, 0.7) 40%, transparent 80%);
  border-radius: 50%;
  box-shadow:
    0 0 20px rgba(0, 212, 180, 0.9),
    0 0 40px rgba(0, 212, 180, 0.4),
    inset 0 0 10px rgba(255, 255, 255, 0.3);
  animation: molecularFloat 12s ease-in-out infinite;
}

.molecular-particle:nth-child(1) {
  top: 15%;
  left: 8%;
  animation-delay: 0s;
  animation-duration: 10s;
}
.molecular-particle:nth-child(2) {
  top: 25%;
  left: 85%;
  animation-delay: 1.5s;
  animation-duration: 14s;
}
.molecular-particle:nth-child(3) {
  top: 65%;
  left: 15%;
  animation-delay: 3s;
  animation-duration: 11s;
}
.molecular-particle:nth-child(4) {
  top: 75%;
  left: 75%;
  animation-delay: 4.5s;
  animation-duration: 13s;
}
.molecular-particle:nth-child(5) {
  top: 45%;
  left: 45%;
  animation-delay: 6s;
  animation-duration: 12s;
}

@keyframes molecularFloat {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.7;
    filter: brightness(1);
  }
  20% {
    transform: translate(25px, -15px) scale(1.2);
    opacity: 0.8;
    filter: brightness(1.1);
  }
  40% {
    transform: translate(-15px, -35px) scale(0.9);
    opacity: 0.9;
    filter: brightness(1.2);
  }
  60% {
    transform: translate(-35px, -5px) scale(1.1);
    opacity: 1;
    filter: brightness(1.3);
  }
  80% {
    transform: translate(10px, -25px) scale(0.95);
    opacity: 0.8;
    filter: brightness(1.1);
  }
}

/* 动画关键帧 */
@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 212, 180, 0.6);
  }
  50% {
    box-shadow: 0 0 30px rgba(0, 212, 180, 0.8);
  }
}

@keyframes arrowPulse {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

@keyframes progressShine {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}


@keyframes particleFloat {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    transform: translateY(-100vh) translateX(50px);
    opacity: 0;
  }
}

@keyframes textShimmer {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes underlineGlow {
  0%, 100% {
    opacity: 0.6;
    transform: translateX(-50%) scaleX(0.8);
  }
  50% {
    opacity: 1;
    transform: translateX(-50%) scaleX(1.2);
  }
}

@keyframes arrowGlow {
  /* 发光动画已移除，保留动画定义以避免编译错误 */
}

@keyframes arrowFlow {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes textFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

@keyframes headPulse {
  0%, 100% {
    transform: translateY(-50%) scale(1);
    filter:
      drop-shadow(0 0 20px rgba(0, 212, 180, 0.8))
      drop-shadow(0 0 40px rgba(0, 212, 180, 0.4));
  }
  50% {
    transform: translateY(-50%) scale(1.1);
    filter:
      drop-shadow(0 0 25px rgba(0, 212, 180, 1))
      drop-shadow(0 0 50px rgba(0, 212, 180, 0.6));
  }
}

@keyframes headGlow {
  0%, 100% {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
    transform: translateY(-50%) scale(1);
  }
  50% {
    box-shadow: 0 0 25px rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.2);
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .hero {
    min-height: 90vh;
    padding: 30px 15px;
  }
  
  .comparison-table {
    max-width: 95%;
    padding: 40px 25px;
  }
  
  .stage-content {
    padding: 18px 12px;
  }
  
  .positioning-label {
    font-size: 22px;
  }
}

@media (max-width: 768px) {
  .hero {
    min-height: 85vh;
    padding: 25px 15px;
  }
  
  .positioning-arrow {
    margin-bottom: 35px;
  }
  
  .positioning-label {
    font-size: 20px;
  }
  
  .comparison-table {
    padding: 30px 20px;
    border-radius: 20px;
  }
  
  .x-axis {
    flex-direction: column;
    gap: 25px;
    margin-bottom: 40px;
  }
  
  .connector-arrow {
    display: none;
  }
  
  .comparison-row {
    flex-direction: column;
    gap: 20px;
  }
  
  .y-axis-label {
    min-width: auto;
    width: 100%;
  }
  
  .progress-track,
  .positioning-track {
    width: 100%;
    height: 16px;
  }
  
  .stage-content {
    padding: 16px 10px;
  }
  
  .stage-upper {
    font-size: 15px;
  }
  
  .stage-lower {
    font-size: 13px;
  }
  
  .molecular-particle {
    width: 6px;
    height: 6px;
  }
  
  .particle {
    width: 3px;
    height: 3px;
  }
}

@media (max-width: 480px) {
  .hero {
    min-height: 80vh;
    padding: 20px 12px;
  }
  
  .positioning-arrow {
    margin-bottom: 25px;
  }
  
  .positioning-label {
    font-size: 18px;
  }
  
  .comparison-table {
    padding: 20px 15px;
    border-radius: 16px;
  }
  
  .stage-content {
    padding: 14px 8px;
  }
  
  .stage-upper {
    font-size: 14px;
  }
  
  .stage-lower {
    font-size: 12px;
  }
  
  .label-content {
    padding: 10px 12px;
  }
  
  .label-content span {
    font-size: 13px;
  }
  
  .progress-track,
  .positioning-track {
    height: 10px;
  }
  
  .molecular-particle {
    width: 4px;
    height: 4px;
  }
  
  .particle {
    width: 2px;
    height: 2px;
  }
}

@media (max-width: 360px) {
  .hero {
    padding: 15px 10px;
  }
  
  .positioning-label {
    font-size: 16px;
  }
  
  .comparison-table {
    padding: 15px 10px;
  }
  
  .stage-content {
    padding: 12px 6px;
  }
  
  .stage-upper {
    font-size: 13px;
  }
  
  .stage-lower {
    font-size: 11px;
  }
  
  .label-content span {
    font-size: 12px;
  }
  
  .progress-track,
  .positioning-track {
    height: 12px;
  }
}
.main-content {
  padding: 80px 0;
}

.section {
  margin-bottom: 80px;
}

.section-title {
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  position: relative;
  padding-left: 20px;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 30px;
  background: #00D4B4;
}

.section-subtitle {
  color: #00D4B4;
  font-size: 14px;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.slide-in-left {
  animation: slideInLeft 1s ease forwards;
}

.slide-in-right {
  animation: slideInRight 1s ease forwards;
}

.fade-in-up {
  animation: fadeInUp 1s ease forwards;
}

@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes slideInRight {
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 产品概览样式 */
.overview-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  align-items: start;
}

.overview-text {
  color: #666;
  line-height: 1.8;
  font-size: 14px;
}

.overview-text p {
  margin-bottom: 20px;
}

.key-features {
  margin-top: 30px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.feature-icon {
  font-size: 18px;
}

.feature-text {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.stat-item {
  background: linear-gradient(135deg, #00D4B4 0%, #00B8A3 100%);
  padding: 25px;
  border-radius: 8px;
  text-align: center;
  color: white;
  transition: transform 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-3px);
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  opacity: 0.9;
}

/* 上下分层文字样式 */
.stage-split {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stage-upper {
  color: #00D4B4;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-shadow: 0 2px 5px rgba(0, 212, 180, 0.3);
}

.stage-lower {
  color: #00D4B4;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-shadow: 0 2px 5px rgba(0, 212, 180, 0.3);
}

/* 产品网格样式 */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  border: 1px solid rgba(0, 212, 180, 0.05);
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #00D4B4, #00B894, #00D4B4);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.product-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow: 0 20px 50px rgba(0, 212, 180, 0.15);
  border-color: rgba(0, 212, 180, 0.2);
}

.product-card:hover::before {
  transform: scaleX(1);
}

.product-icon {
  position: relative;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(135deg, #f0f9f8 0%, #e8f5f3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.icon-wrapper {
  position: relative;
  z-index: 2;
}

.product-emoji {
  font-size: 64px;
  display: block;
  filter: drop-shadow(0 4px 8px rgba(0, 212, 180, 0.2));
  transition: all 0.3s ease;
  animation: iconFloat 3s ease-in-out infinite;
}

.product-icon.icon-hover {
  background: linear-gradient(135deg, #e8f5f3 0%, #d1e9e5 100%);
  transform: translateY(-2px);
}

.product-icon.icon-hover .product-emoji {
  transform: scale(1.1) rotate(5deg);
  filter: drop-shadow(0 6px 12px rgba(0, 212, 180, 0.3));
}

@keyframes iconFloat {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
}

.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 212, 180, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-image:hover .product-overlay {
  opacity: 1;
}

.detail-btn {
  background: white;
  color: #00D4B4;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.detail-btn:hover {
  transform: scale(1.05);
}

.product-info {
  padding: 25px;
}

.product-category {
  color: #00D4B4;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.product-name {
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
}

.product-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.product-specs {
  margin-bottom: 20px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.spec-label {
  color: #999;
}

.spec-value {
  color: #333;
  font-weight: 500;
}

.product-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tag {
  background: #f0f9f8;
  color: #00D4B4;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

/* 技术优势样式 */
.advantages-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.advantage-item {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 212, 180, 0.05);
}

.advantage-item::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 212, 180, 0.05) 0%, transparent 70%);
  transform: scale(0);
  transition: transform 0.6s ease;
  border-radius: 50%;
}

.advantage-item:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 15px 40px rgba(0, 212, 180, 0.12);
  border-color: rgba(0, 212, 180, 0.15);
}

.advantage-item:hover::before {
  transform: scale(1);
}

.advantage-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #00D4B4 0%, #00B8A3 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.advantage-icon::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: rotate(45deg);
  transition: transform 0.6s ease;
}

.advantage-item:hover .advantage-icon {
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(0, 212, 180, 0.4);
}

.advantage-item:hover .advantage-icon::before {
  transform: rotate(45deg) translate(100%, 100%);
}

.advantage-emoji {
  font-size: 36px;
  z-index: 2;
  position: relative;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  animation: advantageIconPulse 2s ease-in-out infinite;
}

@keyframes advantageIconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.advantage-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.advantage-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.advantage-metrics {
  display: flex;
  justify-content: space-around;
  gap: 15px;
}

.metric {
  text-align: center;
}

.metric-value {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: #00D4B4;
}

.metric-unit {
  font-size: 14px;
  color: #00D4B4;
}

.metric-label {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

/* 应用场景样式 */
.applications-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-top: 40px;
}

.applications-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.application-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.application-item:hover,
.application-item.active {
  border-color: #00D4B4;
  transform: translateX(5px);
}

.app-icon {
  width: 50px;
  height: 50px;
  background: #f0f9f8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.app-icon img {
  width: 25px;
  height: 25px;
}

.app-content h4 {
  font-size: 16px;
  color: #333;
  margin-bottom: 5px;
  font-weight: 600;
}

.app-content p {
  font-size: 13px;
  color: #666;
}

.application-detail {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.detail-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 20px;
}

.detail-content h3 {
  font-size: 20px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.detail-content p {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.detail-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-feature {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #333;
}

.feature-check {
  color: #00D4B4;
  font-weight: bold;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container1 {
    flex-direction: column;
    text-align: center;
    gap: 40px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .petri-dish-container {
    width: 300px;
    height: 300px;
  }

  .petri-dish {
    width: 250px;
    height: 250px;
  }

  .petri-lid {
    width: 270px;
    height: 270px;
    top: -10px;
    left: -10px;
  }

  .overview-content,
  .applications-showcase {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .products-grid,
  .advantages-container,
  .certification-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .key-features,
  .detail-features {
    grid-template-columns: 1fr;
  }

  .overview-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .petri-dish-container {
    width: 250px;
    height: 250px;
  }

  .petri-dish {
    width: 200px;
    height: 200px;
  }

  .petri-lid {
    width: 220px;
    height: 220px;
  }

  .overview-stats {
    grid-template-columns: 1fr;
  }
}

/* 增强的动画效果和视觉特效 */
@keyframes molecularFloat {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  25% {
    transform: translate(30px, -20px) scale(1.2);
  }
  50% {
    transform: translate(-20px, 30px) scale(0.8);
    opacity: 0.8;
  }
  75% {
    transform: translate(40px, 10px) scale(1.1);
  }
  90% {
    opacity: 1;
  }
}

/* 增强的粒子动画 */
.particle {
  background: radial-gradient(circle, rgba(0, 212, 180, 0.6) 0%, rgba(0, 184, 148, 0.3) 50%, transparent 100%);
  box-shadow: 0 0 15px rgba(0, 212, 180, 0.4);
}

/* 增强的标题动画效果 */
.hero-title {
  background: linear-gradient(135deg, #00D4B4 0%, #00B894 50%, #00D4B4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 20px rgba(0, 212, 180, 0.3);
  animation: titleShine 3s ease-in-out infinite alternate;
}

@keyframes titleShine {
  0% {
    filter: brightness(1);
    transform: translateY(0);
  }
  50% {
    filter: brightness(1.2);
    transform: translateY(-2px);
  }
  100% {
    filter: brightness(1.1);
    transform: translateY(0);
  }
}

/* 增强的描述文字动画 */
.hero-description {
  animation: descriptionFadeIn 1.5s ease-out 0.8s both;
  text-shadow: 0 1px 10px rgba(0, 212, 180, 0.1);
}

@keyframes descriptionFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 增强的对比表格动画 */
.comparison-item {
  animation: tableItemSlideIn 0.6s ease-out both;
  transform: translateX(-30px);
  opacity: 0;
}

.comparison-item:nth-child(1) { animation-delay: 0.1s; }
.comparison-item:nth-child(2) { animation-delay: 0.2s; }
.comparison-item:nth-child(3) { animation-delay: 0.3s; }
.comparison-item:nth-child(4) { animation-delay: 0.4s; }
.comparison-item:nth-child(5) { animation-delay: 0.5s; }
.comparison-item:nth-child(6) { animation-delay: 0.6s; }

@keyframes tableItemSlideIn {
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 增强的检测卡片动画 */
.detection-card {
  animation: cardFadeInUp 0.8s ease-out both;
  transform: translateY(40px);
  opacity: 0;
}

.detection-card:nth-child(1) { animation-delay: 0.2s; }
.detection-card:nth-child(2) { animation-delay: 0.4s; }
.detection-card:nth-child(3) { animation-delay: 0.6s; }

@keyframes cardFadeInUp {
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 玻璃拟态增强效果 */
.comparison-table {
  background:
    linear-gradient(135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    0 8px 32px 0 rgba(31, 38, 135, 0.37),
    inset 0 0 20px rgba(255, 255, 255, 0.1);
}


/* 增强的分子交互效果 */
.molecular-particle:hover {
  transform: scale(1.5);
  box-shadow: 0 0 25px rgba(0, 212, 180, 1);
  transition: all 0.3s ease;
}

/* 优化的响应式分子效果 */
@media (max-width: 768px) {
  .molecular-particle {
    width: 4px;
    height: 4px;
  }
  
  .particle {
    width: 3px;
    height: 3px;
  }
}

@media (max-width: 480px) {
  .molecular-particle {
    width: 3px;
    height: 3px;
  }
  
  .particle {
    width: 2px;
    height: 2px;
  }
}
</style>